"use client";

import { ProvidersEnum, RequestError, CasinoGameTypeEnum } from "@finbackoffice/fe-core";
import {
    UseCasinoGamesHookOptions,
    useCasinoGames as useCoreCasinoGames,
} from "@finbackoffice/site-core";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo } from "react";
import { CasinoGameTag } from "@finbackoffice/enums";
import { useDevice, useNotification } from "hooks";

export const useCasinoGames = (
    gameTypes: CasinoGameTypeEnum[],
    tag: CasinoGameTag | "",
    provider: ProvidersEnum | "All",
    options?: UseCasinoGamesHookOptions,
) => {
    const { t } = useTranslation();
    const { notification, setNotification, clearNotification } = useNotification();
    const { isMobileDevice } = useDevice();

    const onErrorHandler = useCallback(
        (error: RequestError) => {
            setNotification({
                type: "error",
                error: error?.error,
                message: error?.message,
            });
        },
        [setNotification],
    );
    const optionsMemo = useMemo(
        () => ({ ...options, mobile: isMobileDevice, onError: onErrorHandler }),
        [isMobileDevice, onErrorHandler, options],
    );
    const { casinoGamesState, pagination, setPagination, hasMore } = useCoreCasinoGames(
        gameTypes,
        tag,
        provider,
        optionsMemo,
    );

    useEffect(() => {
        if (casinoGamesState.loading) {
            clearNotification();
        } else if (!casinoGamesState.games.length) {
            setNotification({
                type: "info",
                error: "",
                message: t("no_games"),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [casinoGamesState.loading]);

    return {
        casinoGamesState,
        notification,
        pagination,
        setPagination,
        hasMore,
    };
};
